import React, { useState, useEffect, useRef } from "react";
import { MoreVertical, Search, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { chatList } from "../components/chatList";

const ChatHeader = ({
  chatWith,
  showSearch,
  setShowSearch,
  search,
  setSearch,
  clearAllMessages,
  scrollToSearchedMessage,
  isMobile,
  onBack
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef(null);
  const user = chatList.find(u => u.id === chatWith);

  const handleSearchClick = () => {
    setShowSearch(true);
    setDropdownOpen(false);
  };

  const handleClearClick = () => {
    clearAllMessages();
    setDropdownOpen(false);
  };

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSearch(false);
      setSearch("");
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      scrollToSearchedMessage(() => {
        setShowSearch(false);
        setSearch("");
      });
    }
  };

  useEffect(() => {
    if (showSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch]);

  const getLastSeenText = () => {
    if (user?.isOnline) return "Online";
    if (!user?.lastSeen) return "Offline";
    const time = new Date(user.lastSeen).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `Last seen at ${time}`;
  };

  return (
    <div className="p-5 flex justify-between items-center border-b relative bg-white z-20">
      <div className="flex items-center gap-3">
        {isMobile && (
          <button
            onClick={onBack}
            className="text-gray-700 hover:text-black p-1"
            aria-label="Back"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        {user?.avatar && (
          <div className="relative">
            <img
              src={user.avatar}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover border"
            />
            <span
              className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                user?.isOnline ? "bg-green-500" : "bg-gray-400"
              }`}
            />
          </div>
        )}
        <div>
          <h2 className="font-bold text-lg text-gray-800">{user?.name}</h2>
          <p className="text-xs text-gray-500">{getLastSeenText()}</p>
        </div>
      </div>

      <div className="relative">
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="focus:outline-none hover:bg-gray-100 rounded-full p-2 transition"
        >
          <MoreVertical className="w-5 h-5 text-gray-600" />
        </button>

        <AnimatePresence>
          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50"
            >
              <button
                onClick={handleSearchClick}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                <Search className="w-4 h-4" /> Search Messages
              </button>
              <button
                onClick={handleClearClick}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition"
              >
                <Trash2 className="w-4 h-4" /> Clear Chat
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showSearch && (
          <motion.input
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            ref={inputRef}
            type="text"
            placeholder="Search message..."
            className="absolute left-1/2 top-full transform -translate-x-1/2 mt-2 px-4 py-2 w-80 rounded-lg border border-gray-300 shadow-lg text-sm bg-white z-30 focus:outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={handleKeyDown}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatHeader;
